import { Agent } from '@atproto/api'
import type { OAuthSession } from '@atproto/oauth-client-browser'
import { MACKINAC_XRPC_URL } from '@/constants/api'
import { getServiceAuthToken } from '@/services/mackinacClient'
import { withMastodonServerSession } from '@/services/mastodonBioService'
import type { MastodonSession } from '@/lib/mastodon'
import { canonicalizeInstanceOrigin, mastodonFetchJsonWithStatus } from '@/lib/mastodonFetch'
import type { PushPreferences, PushRegistrationRecord } from '@/types/push'

/**
 * Push registration calls for the three places a browser subscription has to
 * be announced:
 * - AT accounts: `social.mackinac.push.*` XRPC procedures on xrpc-server
 *   (service-auth token per call, like userPreferencesService).
 * - Mastodon accounts, step 1: xrpc-server `/mastodon/push/*` with the
 *   Mastodon server-session JWT. Returns the ingest endpoint + keys exactly
 *   once (reveal-once contract).
 * - Mastodon accounts, step 2: the user's own instance
 *   `POST /api/v1/push/subscription`, pointed at that ingest endpoint.
 */

const REGISTER_NSID = 'social.mackinac.push.registerSubscription'
const REMOVE_NSID = 'social.mackinac.push.removeSubscription'
const GET_PREFS_NSID = 'social.mackinac.push.getPreferences'
const SET_PREFS_NSID = 'social.mackinac.push.setPreferences'

const ENABLED_STORAGE_KEY = 'mackinac-push-enabled'
const RECORDS_STORAGE_KEY = 'mackinac-push-registrations'

/** Removal on account disconnect must never hold up sign-out for long. */
const ACCOUNT_REMOVAL_TIMEOUT_MS = 5_000

/**
 * Core Mastodon alert set. `status` is off: it would push every post from
 * accounts with the bell enabled. Per-type filtering happens in the
 * notifications service, so everything else is requested.
 */
const MASTODON_ALERTS = {
    mention: true,
    status: false,
    reblog: true,
    follow: true,
    follow_request: true,
    favourite: true,
    poll: true,
    update: false,
} as const

export interface WebPushSubscriptionKeys {
    endpoint: string
    keys: { p256dh: string; auth: string }
}

export interface MastodonPushBlock {
    endpoint: string
    keys: { p256dh: string; auth: string }
    standard: boolean
}

export type MastodonRegisterResult =
    | { kind: 'created'; mastodon: MastodonPushBlock }
    | { kind: 'exists' }
    | { kind: 'unavailable' }

export class PushRequestError extends Error {
    readonly status: number

    constructor(operation: string, status: number) {
        super(`${operation} failed: ${status}`)
        this.name = 'PushRequestError'
        this.status = status
    }
}

/** Extract endpoint + keys from `PushSubscription.toJSON()`; throws if incomplete. */
export function toWebPushSubscription(sub: PushSubscriptionJSON): WebPushSubscriptionKeys {
    const { endpoint, keys } = sub
    if (!endpoint || !keys?.p256dh || !keys.auth) {
        throw new Error('Push subscription is missing its endpoint or keys')
    }
    return { endpoint, keys: { p256dh: keys.p256dh, auth: keys.auth } }
}

// --- AT Protocol (xrpc-server procedures) ---------------------------------

async function atRequest(
    agent: Agent,
    nsid: string,
    body?: Record<string, unknown>
): Promise<unknown> {
    const token = await getServiceAuthToken(agent, nsid)
    const response = await fetch(`${MACKINAC_XRPC_URL}/xrpc/${nsid}`, {
        method: body ? 'POST' : 'GET',
        headers: {
            Authorization: `Bearer ${token}`,
            ...(body ? { 'Content-Type': 'application/json' } : {}),
        },
        ...(body ? { body: JSON.stringify(body) } : {}),
    })
    if (!response.ok) throw new PushRequestError(nsid, response.status)
    return response.json()
}

export async function registerAtSubscription(
    agent: Agent,
    sub: WebPushSubscriptionKeys
): Promise<void> {
    await atRequest(agent, REGISTER_NSID, {
        platform: 'web',
        endpoint: sub.endpoint,
        transport: { p256dh: sub.keys.p256dh, auth: sub.keys.auth },
    })
}

export async function removeAtSubscription(agent: Agent, endpoint: string): Promise<void> {
    await atRequest(agent, REMOVE_NSID, { endpoint })
}

function readPreferences(body: unknown): PushPreferences {
    const prefs = (body as { preferences?: unknown } | null)?.preferences
    return typeof prefs === 'object' && prefs !== null ? (prefs as PushPreferences) : {}
}

export async function getAtPreferences(agent: Agent): Promise<PushPreferences> {
    return readPreferences(await atRequest(agent, GET_PREFS_NSID))
}

export async function setAtPreferences(
    agent: Agent,
    preferences: PushPreferences
): Promise<PushPreferences> {
    return readPreferences(await atRequest(agent, SET_PREFS_NSID, { preferences }))
}

// --- Mastodon via xrpc-server ----------------------------------------------

function mastodonServerRequest(
    session: MastodonSession,
    path: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    body?: Record<string, unknown>
): Promise<Response> {
    return withMastodonServerSession(session, token =>
        fetch(`${MACKINAC_XRPC_URL}/mastodon/push/${path}`, {
            method,
            headers: {
                Authorization: `Bearer ${token}`,
                ...(body ? { 'Content-Type': 'application/json' } : {}),
            },
            ...(body ? { body: JSON.stringify(body) } : {}),
        })
    )
}

function isMastodonPushBlock(value: unknown): value is MastodonPushBlock {
    const block = value as Partial<MastodonPushBlock> | null | undefined
    return (
        typeof block?.endpoint === 'string' &&
        isHttpsUrl(block.endpoint) &&
        typeof block.keys?.p256dh === 'string' &&
        typeof block.keys.auth === 'string'
    )
}

function isHttpsUrl(value: string): boolean {
    try {
        return new URL(value).protocol === 'https:'
    } catch {
        return false
    }
}

/**
 * 201 → `created` with the reveal-once ingest block; 200 → `exists` (the row
 * already had keys — the caller must DELETE then POST to get fresh ones);
 * 503 → `unavailable` (push not configured server-side yet, mackinac#2776).
 */
export async function registerMastodonSubscription(
    session: MastodonSession,
    sub: WebPushSubscriptionKeys
): Promise<MastodonRegisterResult> {
    const response = await mastodonServerRequest(session, 'subscription', 'POST', {
        platform: 'web',
        endpoint: sub.endpoint,
        transport: { p256dh: sub.keys.p256dh, auth: sub.keys.auth },
    })
    if (response.status === 503) return { kind: 'unavailable' }
    if (!response.ok) throw new PushRequestError('registerMastodonSubscription', response.status)
    const body = (await response.json().catch(() => null)) as { mastodon?: unknown } | null
    if (response.status === 201 && isMastodonPushBlock(body?.mastodon)) {
        return { kind: 'created', mastodon: body.mastodon }
    }
    return { kind: 'exists' }
}

export async function removeMastodonSubscription(
    session: MastodonSession,
    endpoint: string
): Promise<void> {
    const response = await mastodonServerRequest(session, 'subscription', 'DELETE', { endpoint })
    if (!response.ok) throw new PushRequestError('removeMastodonSubscription', response.status)
}

export async function getMastodonPreferences(session: MastodonSession): Promise<PushPreferences> {
    const response = await mastodonServerRequest(session, 'preferences', 'GET')
    if (!response.ok) throw new PushRequestError('getMastodonPreferences', response.status)
    return readPreferences(await response.json())
}

export async function setMastodonPreferences(
    session: MastodonSession,
    preferences: PushPreferences
): Promise<PushPreferences> {
    const response = await mastodonServerRequest(session, 'preferences', 'PUT', { preferences })
    if (!response.ok) throw new PushRequestError('setMastodonPreferences', response.status)
    return readPreferences(await response.json())
}

// --- Mastodon instance (/api/v1/push/subscription) -------------------------

function instancePushUrl(session: MastodonSession): { url: string; origin: string } {
    const { origin } = canonicalizeInstanceOrigin(session.instance_url)
    return { url: `${origin}/api/v1/push/subscription`, origin }
}

/**
 * Point the instance's push subscription for this access token at the
 * notifications-service ingest endpoint. Instances that reject the
 * `standard` parameter (pre-4.4) get one retry without it.
 */
export async function subscribeInstancePush(
    session: MastodonSession,
    block: MastodonPushBlock
): Promise<{ endpoint: string }> {
    const { url, origin } = instancePushUrl(session)
    const send = (standard: boolean) =>
        mastodonFetchJsonWithStatus<{ endpoint?: string }>(
            url,
            {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${session.access_token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    subscription: {
                        endpoint: block.endpoint,
                        keys: { p256dh: block.keys.p256dh, auth: block.keys.auth },
                        ...(standard ? { standard: true } : {}),
                    },
                    data: { alerts: MASTODON_ALERTS, policy: 'all' },
                }),
            },
            { expectedOrigin: origin }
        )

    let result = await send(block.standard)
    if (!result.ok && result.status === 422 && block.standard) {
        result = await send(false)
    }
    if (!result.ok) throw new PushRequestError('subscribeInstancePush', result.status)
    return { endpoint: result.data.endpoint ?? block.endpoint }
}

/** The instance's current push subscription for this token, or null when none (404). */
export async function getInstancePushSubscription(
    session: MastodonSession
): Promise<{ endpoint: string } | null> {
    const { url, origin } = instancePushUrl(session)
    const result = await mastodonFetchJsonWithStatus<{ endpoint?: string }>(
        url,
        { headers: { Authorization: `Bearer ${session.access_token}` } },
        { expectedOrigin: origin }
    )
    if (!result.ok) {
        if (result.status === 404) return null
        throw new PushRequestError('getInstancePushSubscription', result.status)
    }
    return typeof result.data.endpoint === 'string' ? { endpoint: result.data.endpoint } : null
}

export async function deleteInstancePush(session: MastodonSession): Promise<void> {
    const { url, origin } = instancePushUrl(session)
    const result = await mastodonFetchJsonWithStatus<unknown>(
        url,
        { method: 'DELETE', headers: { Authorization: `Bearer ${session.access_token}` } },
        { expectedOrigin: origin }
    )
    if (!result.ok && result.status !== 404) {
        throw new PushRequestError('deleteInstancePush', result.status)
    }
}

// --- Local registration state ---------------------------------------------

export function readPushEnabled(): boolean {
    try {
        return localStorage.getItem(ENABLED_STORAGE_KEY) === 'true'
    } catch {
        return false
    }
}

export function writePushEnabled(enabled: boolean): void {
    try {
        if (enabled) localStorage.setItem(ENABLED_STORAGE_KEY, 'true')
        else localStorage.removeItem(ENABLED_STORAGE_KEY)
    } catch {
        // localStorage unavailable — state lives for this page only
    }
}

function isRecord(value: unknown): value is PushRegistrationRecord {
    const r = value as Partial<PushRegistrationRecord> | null
    return (
        typeof r?.accountKey === 'string' &&
        (r.protocol === 'atproto' || r.protocol === 'mastodon') &&
        typeof r.endpoint === 'string' &&
        typeof r.registeredAt === 'string'
    )
}

/** SHA-256 (hex) of a Mastodon ingest endpoint URL; what records store and compare. */
export async function hashIngestEndpoint(endpoint: string): Promise<string> {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(endpoint))
    return Array.from(new Uint8Array(digest), b => b.toString(16).padStart(2, '0')).join('')
}

const SHA256_HEX = /^[0-9a-f]{64}$/

/** Current-shape view of a stored record: the legacy raw endpoint is never surfaced. */
function toRecord(value: PushRegistrationRecord & { mastodonIngestEndpoint?: unknown }) {
    const { mastodonIngestEndpoint: _legacyRaw, mastodonIngestEndpointHash, ...rest } = value
    void _legacyRaw
    return {
        ...rest,
        ...(typeof mastodonIngestEndpointHash === 'string' &&
        SHA256_HEX.test(mastodonIngestEndpointHash)
            ? { mastodonIngestEndpointHash }
            : {}),
    } as PushRegistrationRecord
}

function readStoredEntries(): (PushRegistrationRecord & { mastodonIngestEndpoint?: unknown })[] {
    try {
        const parsed: unknown = JSON.parse(localStorage.getItem(RECORDS_STORAGE_KEY) ?? '[]')
        return Array.isArray(parsed) ? parsed.filter(isRecord) : []
    } catch {
        return []
    }
}

export function readPushRegistrations(): PushRegistrationRecord[] {
    return readStoredEntries().map(toRecord)
}

/**
 * One-time migration of records written before only the hash was stored: the
 * raw `mastodonIngestEndpoint` is hashed, the hash kept, and the raw value
 * dropped from storage. Because the hash is of the same string the old code
 * compared, already-registered users are not forced to re-register.
 */
export async function migrateLegacyIngestEndpoints(): Promise<void> {
    const legacy = new Map<string, string>()
    for (const entry of readStoredEntries()) {
        if (typeof entry.mastodonIngestEndpoint === 'string' && entry.mastodonIngestEndpoint) {
            legacy.set(
                entry.mastodonIngestEndpoint,
                await hashIngestEndpoint(entry.mastodonIngestEndpoint)
            )
        }
    }
    // Re-read after the awaits (no await below) so a concurrent write isn't clobbered.
    const entries = readStoredEntries()
    if (!entries.some(e => 'mastodonIngestEndpoint' in e)) return
    writePushRegistrations(
        entries.map(entry => {
            const raw = entry.mastodonIngestEndpoint
            const hash = typeof raw === 'string' ? legacy.get(raw) : undefined
            const record = toRecord(entry)
            return hash && !record.mastodonIngestEndpointHash
                ? { ...record, mastodonIngestEndpointHash: hash }
                : record
        })
    )
}

export function writePushRegistrations(records: PushRegistrationRecord[]): void {
    try {
        if (records.length === 0) localStorage.removeItem(RECORDS_STORAGE_KEY)
        else localStorage.setItem(RECORDS_STORAGE_KEY, JSON.stringify(records))
    } catch {
        // localStorage unavailable
    }
}

/** Remove one record's server-side registration(s) using the account's own auth. */
export async function removeRegistrationRemote(
    record: PushRegistrationRecord,
    auth: { agent?: Agent; mastodonSession?: MastodonSession }
): Promise<void> {
    if (record.protocol === 'atproto') {
        if (!auth.agent) throw new Error('No AT session for push removal')
        await removeAtSubscription(auth.agent, record.endpoint)
        return
    }
    if (!auth.mastodonSession) throw new Error('No Mastodon session for push removal')
    const results = await Promise.allSettled([
        removeMastodonSubscription(auth.mastodonSession, record.endpoint),
        deleteInstancePush(auth.mastodonSession),
    ])
    const failed = results.find(r => r.status === 'rejected')
    if (failed) throw (failed as PromiseRejectedResult).reason
}

/**
 * Kill this browser's push endpoint. The push service then answers 404/410,
 * and the notifications service deletes every row for it. The hook's next
 * reconcile re-subscribes and re-registers the accounts that remain.
 */
async function dropBrowserSubscription(): Promise<void> {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
    const registration = await navigator.serviceWorker.getRegistration()
    const sub = await registration?.pushManager.getSubscription()
    await sub?.unsubscribe()
}

/**
 * Best-effort unregister for an account that is about to be disconnected.
 * Once its session is gone there is no auth left to remove the server row.
 * It only does anything when the push hook previously registered this account
 * (records are only ever written behind the `pushNotifications` flag).
 *
 * If removal fails or times out (expired session, slow instance), the browser
 * subscription is dropped instead. Otherwise this device would keep receiving
 * the removed account's notifications with nothing able to stop them. Never
 * throws; the network part is bounded by ACCOUNT_REMOVAL_TIMEOUT_MS.
 */
export async function unregisterPushForAccount(
    accountKey: string,
    auth: { blueskySession?: OAuthSession; mastodonSession?: MastodonSession }
): Promise<void> {
    const records = readPushRegistrations()
    const mine = records.filter(r => r.accountKey === accountKey)
    if (mine.length === 0) return
    writePushRegistrations(records.filter(r => r.accountKey !== accountKey))

    const agent = auth.blueskySession ? new Agent(auth.blueskySession) : undefined
    const work = Promise.allSettled(
        mine.map(record =>
            removeRegistrationRemote(record, { agent, mastodonSession: auth.mastodonSession })
        )
    ).then(results => results.every(r => r.status === 'fulfilled'))
    let timer: ReturnType<typeof setTimeout> | undefined
    const timeout = new Promise<boolean>(resolve => {
        timer = setTimeout(() => resolve(false), ACCOUNT_REMOVAL_TIMEOUT_MS)
    })
    try {
        const removed = await Promise.race([work, timeout])
        if (!removed) await dropBrowserSubscription()
    } catch {
        // best effort
    } finally {
        clearTimeout(timer)
    }
}
